import React from "react";
import styled, { css } from "styled-components";
import PropTypes from "prop-types";

// context
import { useSearchBar } from "core/SearchBar/context/useSearchBar.js";

// components
import Search from "core/ReusableIcons/Search";

// styles
import { regular } from "core/fontStyles";
import colors from "core/colors";
import { convertPxToRem } from "frontend/components/helpers";
import { UnstyledButton } from "core/Button/styles";
import { srOnlyStyles } from "core/SrOnly";
import breakpoints from "core/breakpoints";
import { getBorderStyles } from "../index";

const SearchButtonStyled = styled(UnstyledButton)`
    ${({ $buttonColors }) => {
        const { color, hoverColor, bgColor, hoverBgColor } = $buttonColors;

        return css`
            flex: 0 0 ${convertPxToRem(40)};
            @media (min-width: ${breakpoints.tablet.rem}) {
                flex: 0 0 ${convertPxToRem(135)};
            }

            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;

            cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

            color: ${color};
            background-color: ${bgColor};

            svg path {
                fill: ${color};
            }

            &:focus {
                outline: none;
                border: ${({ $borderStyles }) => $borderStyles.focusBorder};
            }

            &:hover {
                color: ${hoverColor};
                background-color: ${hoverBgColor};

                svg path {
                    fill: ${hoverColor};
                }
            }
        `;
    }}
`;

const TextStyled = styled.span`
    ${regular}
    display: inline-block;
    font-size: ${convertPxToRem(15)};
    margin-right: ${convertPxToRem(8)};
    @media (max-width: ${breakpoints.beforeTablet.rem}) {
        ${srOnlyStyles}
    }
`;

const getButtonColors = isDarkMode => {
    // color
    const color = isDarkMode ? colors.secondary.orca : colors.primary.diamond;
    const hoverColor = isDarkMode
        ? colors.primary.diamond
        : colors.primary.diamond;

    // background
    const bgColor = isDarkMode
        ? colors.accent.darkMode.suenosBuenos
        : colors.secondary.orca;

    const hoverBgColor = isDarkMode
        ? colors.accent.darkMode.floridaWater
        : colors.secondary.slate;

    return {
        color,
        hoverColor,
        bgColor,
        hoverBgColor
    };
};

// component
export const SearchButton = ({ className, showText = true, iconSize = 16 }) => {
    const { isDarkMode, isDisabled } = useSearchBar();

    return (
        <SearchButtonStyled
            className={className}
            $buttonColors={getButtonColors(isDarkMode)}
            $borderStyles={getBorderStyles(isDarkMode)}
            type="submit"
            aria-label="Submit search term"
            disabled={isDisabled}
        >
            {showText && <TextStyled>Search</TextStyled>}
            <Search size={{ width: iconSize, height: iconSize }} />
        </SearchButtonStyled>
    );
};

export default SearchButton;

// prop-types
SearchButton.propTypes = {
    className: PropTypes.string,
    showText: PropTypes.bool,
    iconSize: PropTypes.number
};
